'use client'

import { useEffect, useRef, useState } from 'react'

import {
  getMyCarReviewStatus,
  updateMyCarReviewScore,
} from '../_apis/getMyCarReview'
import {
  MACAR_SERVER_ID_PARAM,
  OWNER_RATING_DEFAULT_SCORE,
  OWNER_REVIEW_LIST_DEEPLINK,
} from '../_constants'

import type { IMyCarReviewStatus } from '../_apis/getMyCarReview'
import type { IModoocarDetail } from '../_types'

/** 노출 중인 다이얼로그 — rating(별점) / complete(평가 완료) / reviewPrompt(리뷰 작성 유도) */
export type TOwnerReviewDialog = 'rating' | 'complete' | 'reviewPrompt' | null

interface IUseOwnerReviewDialogsParams {
  /** 인앱 웹뷰 여부 — 아닐 때는 어떤 조회/노출도 하지 않는다 */
  enabled: boolean
  /** 내 차 식별자 — 앱이 웹뷰 요청 헤더(x-mycle-macar-id)로 전달 (서버에서 prop으로 내려줌) */
  macarId?: string | null
  /** 보고 있는 차종 — 오너 리뷰 리스트 딥링크에 실어 보낸다 */
  vehicle: Pick<IModoocarDetail, 'brand' | 'upperModelName'>
}

/**
 * 내 차 식별자 세션 저장 키. 상세↔랭킹은 next/link 소프트 네비라 재진입 시
 * RSC fetch에 앱 헤더(x-mycle-macar-id)가 실리지 않아 macarId prop이 유실된다.
 * 헤더가 살아있는 로드(콜드오픈/하드 로드)에서 저장해 두고 이후 소프트 네비에서 재사용한다.
 */
const MACAR_SERVER_ID_STORAGE_KEY = 'modoocar:macarServerId'

/** 유효한 식별자(숫자 문자열)인지 검증 */
const isValidMacarId = (value: string | null | undefined): value is string =>
  Boolean(value && /^\d+$/.test(value))

/**
 * 내 차 식별자 결정 — 우선순위:
 * 1. 앱 헤더 prop(x-mycle-macar-id) — 있으면 세션에도 저장
 * 2. URL 쿼리(macarServerId) 폴백 (일반 브라우저 QA용 — ?webview=1과 병용)
 * 3. 세션 폴백 — 소프트 네비(랭킹↔상세)로 헤더가 유실됐을 때 재사용
 */
const resolveMacarServerId = (macarId?: string | null): string | null => {
  if (isValidMacarId(macarId)) {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(MACAR_SERVER_ID_STORAGE_KEY, macarId)
    }
    return macarId
  }
  if (typeof window === 'undefined') return null
  const queryValue = new URLSearchParams(window.location.search).get(
    MACAR_SERVER_ID_PARAM
  )
  if (isValidMacarId(queryValue)) return queryValue
  const storedValue = window.sessionStorage.getItem(MACAR_SERVER_ID_STORAGE_KEY)
  return isValidMacarId(storedValue) ? storedValue : null
}

/**
 * 인앱 오너 만족도 평가/리뷰 다이얼로그 상태·흐름 (Figma 9064:32674)
 *
 * - 진입 시: 내 차 리뷰 조회 → 리뷰(별점)가 없으면 별점 팝업 노출 (진입할 때마다)
 * - 별점 확인: 별점 저장(updateReview) 성공 시 [평가 완료] 팝업 노출
 * - 오너 리뷰 전체보기 클릭: 리뷰가 없거나 리뷰 글(title)이 비어 있으면 리뷰 팝업 노출
 */
export const useOwnerReviewDialogs = ({
  enabled,
  macarId,
  vehicle,
}: IUseOwnerReviewDialogsParams) => {
  const [activeDialog, setActiveDialog] = useState<TOwnerReviewDialog>(null)
  const [score, setScore] = useState(OWNER_RATING_DEFAULT_SCORE)
  const [isSubmitting, setIsSubmitting] = useState(false)
  // 진입 조회 결과 캐시 — 전체보기 클릭 시 재조회 없이 판단 (별점 저장 시 갱신)
  const statusRef = useRef<IMyCarReviewStatus | null>(null)
  const checkedRef = useRef(false)

  // 진입 시 1회: 내 차 리뷰 조회 → 별점 미평가 유저에게 별점 팝업 노출
  useEffect(() => {
    if (!enabled || checkedRef.current) return
    const macarServerId = resolveMacarServerId(macarId)
    if (!macarServerId) return
    checkedRef.current = true

    let cancelled = false
    getMyCarReviewStatus(macarServerId)
      .then(status => {
        if (cancelled) return
        statusRef.current = status
        if (!status.hasReview) setActiveDialog('rating')
      })
      .catch(() => {
        // 조회 실패 시 팝업 미노출 (콘텐츠 열람에 지장 없도록)
      })
    return () => {
      cancelled = true
    }
  }, [enabled, macarId])

  const closeDialog = () => setActiveDialog(null)

  /** 별점 팝업 '확인' — 별점 저장 성공 시 [평가 완료] 팝업으로 전환 */
  const confirmRating = async () => {
    const macarServerId = resolveMacarServerId(macarId)
    if (!macarServerId || isSubmitting) return
    setIsSubmitting(true)
    try {
      await updateMyCarReviewScore({ macarServerId, score })
      statusRef.current = {
        hasReview: true,
        hasReviewText: statusRef.current?.hasReviewText ?? false,
      }
      setActiveDialog('complete')
    } catch {
      // 저장 실패 시 팝업만 닫는다 (다음 진입 시 재노출)
      setActiveDialog(null)
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * 오너 리뷰 '전체보기' 클릭 (Figma 9064:32674):
   * - 리뷰 미작성(없음 또는 title 비어 있음) → 리뷰 작성 유도 팝업 노출
   * - 리뷰 남긴 기록 있음 → 보고 있는 차종의 오너 리뷰 리스트 앱 딥링크 이동
   * 인앱이 아니거나 식별자가 없으면 아무것도 하지 않는다.
   */
  const openReviewPromptIfNeeded = async () => {
    if (!enabled) return
    const macarServerId = resolveMacarServerId(macarId)
    if (!macarServerId) return

    // 네이티브 carreview/write에서 리뷰를 작성하고 돌아와도 웹뷰가 살아 있어(재로드 X)
    // statusRef 캐시가 진입 시점 값으로 stale 해진다. 전체보기 시점엔 캐시를 신뢰하지 않고
    // 항상 최신 리뷰 상태를 재조회해, 작성 완료된 리뷰가 반영되도록 한다.
    const status = await getMyCarReviewStatus(macarServerId).catch(() => null)
    if (!status) return
    statusRef.current = status

    if (!status.hasReview || !status.hasReviewText) {
      setActiveDialog('reviewPrompt')
      return
    }
    window.location.href = `${OWNER_REVIEW_LIST_DEEPLINK}/${encodeURIComponent(vehicle.upperModelName)}/${encodeURIComponent(vehicle.brand)}`
  }

  return {
    activeDialog,
    score,
    setScore,
    isSubmitting,
    closeDialog,
    confirmRating,
    openReviewPromptIfNeeded,
  }
}
